import { ErrorBoundary, NoAccess } from "@neonumy/ant-design";
import { APP_URLS } from "@neonumy/api-clients";
import { Suspense, lazy } from "react";
import { Route, createBrowserRouter, createRoutesFromElements } from "react-router-dom";
import LoadingPage from "views/Base/LoadingPage";
import CatalogOutlet from "views/Catalog/CatalogOutlet";
import MainOutlet from "views/MainOutlet";
import SettingsOutlet from "views/Settings/SettingsOutlet";
import DeprecatedRouteRedirect from "./DeprecatedRouteRedirect";
import PrivateRoute from "./PrivateRoute";

const Page404 = lazy(() => import("views/Base/Page404"));
const Budget = lazy(() => import("views/public/Budget"));
const Home = lazy(() => import("views/Home"));
const LandingPageWithoutContent = lazy(() => import("views/Base/LandingPageWithoutContent"));

const ClientsTabs = lazy(() => import("views/Clients/components/ClientsTabs"));
const ClientsTable = lazy(() => import("views/Clients/components/DataTable"));
// const SaleDocument = lazy(() => import("views/Documents/SaleDocument"));
const SaleDocumentIndex = lazy(() => import("views/Documents/SaleDocument/SaleDocumentIndex"));
const Preview = lazy(() => import("views/Documents/Preview"));
const Documents = lazy(() => import("views/Documents"));
const SignButton = lazy(
  () => import("views/Documents/components/InvoiceSubmitButtons/components/SignButton"),
);

const routes: ReturnType<typeof createBrowserRouter> = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route
        path="/"
        element={
          <ErrorBoundary>
            <PrivateRoute />
          </ErrorBoundary>
        }
      >
        <Route index element={<LandingPageWithoutContent />} />
        <Route path="accounting-firm/:accountingFirmId" element={<LandingPageWithoutContent />} />
        <Route
          path="accounting-firm/:accountingFirmId/company/:companyId"
          element={<DeprecatedRouteRedirect />}
        />
        <Route path="/company/:companyId" element={<Home />}>
          <Route path=":menu" element={<MainOutlet />}>
            <Route index element={<MainOutlet />} />
            <Route path=":subMenu" element={<CatalogOutlet />} />
            <Route path=":subMenu" element={<SettingsOutlet />} />
            {/* GENERAL ROUTES */}
            <Route path=":subMenu/identity" element={<SettingsOutlet />} />
            <Route path=":subMenu/payment-settings" element={<SettingsOutlet />} />
            <Route path=":subMenu/formats" element={<SettingsOutlet />} />
            <Route path=":subMenu/features" element={<SettingsOutlet />} />
            {/* CUSTOMIZATION ROUTES */}
            <Route path=":subMenu/template" element={<SettingsOutlet />} />
            <Route path=":subMenu/email" element={<SettingsOutlet />} />
            <Route path=":subMenu/attachments" element={<SettingsOutlet />} />
            {/* ACCOUNTING ROUTES */}
            <Route path=":subMenu/vats" element={<SettingsOutlet />} />
            <Route path=":subMenu/sales" element={<SettingsOutlet />} />
            <Route path=":subMenu/account" element={<SettingsOutlet />} />
            <Route path=":subMenu/export-history" element={<SettingsOutlet />} />
          </Route>
        </Route>

        <Route path="/company/:companyId" element={<Home />}>
          <Route path=":menu">
            <Route index element={<ClientsTable />} />
            <Route path=":clientId" element={<ClientsTabs />}>
              <Route path=":subMenu" element={<ClientsTabs />} />
            </Route>
          </Route>

          <Route path=":menu">
            <Route index element={<Documents />} />
            <Route path=":subMenu">
              <Route path="edit/:invoiceId" element={<SaleDocumentIndex />} />
              <Route path="edit/:invoiceId/:sign" element={<SignButton />} />
            </Route>
            <Route path=":invoiceId/preview" element={<Preview />} />
          </Route>
        </Route>
        <Route
          path="*"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Page404 />
            </Suspense>
          }
        />
      </Route>
      <Route
        path="budget/:hash"
        element={
          <Suspense fallback={<LoadingPage />}>
            <Budget />
          </Suspense>
        }
      />
      <Route path="no-access" element={<NoAccess dashboardUrl={APP_URLS.dashboardUrl} />} />
    </>,
  ),
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);

export { routes };
